import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type PaymentMethodCard } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

import { useSubmitPaymentMethodMutation } from './graphql/SubmitPaymentMethod.generated';

/**
 * Depending on the feature flag, this will either:
 * - Submit a Billing Account through monograph-svc.
 * - Submit both Billing Accounts and Payment Method Card through payments-svc.
 */
export function useSubmitPaymentMethod(props: SubmitPaymentMethodProps) {
  const { onSuccess } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Payment Method Card Submission ──────────────────────────────────

  const [submitPaymentMethodCardResponse, submitPaymentMethodCardMutation] =
    useSubmitPaymentMethodMutation();

  const submitPaymentMethodCard = useCallback(
    async (payload: SubmitPaymentMethodPayload) => {
      const response = await submitPaymentMethodCardMutation({
        input: payload,
      });

      const paymentMethod = response.data?.updatePaymentMethod;
      const isSuccess = paymentMethod?.__typename === 'PaymentMethodCard';
      const userError = formatMessage(messages.creditCardFormErrorSave);

      if (isSuccess) {
        return { isSuccess, userError, paymentMethod };
      }

      return { isSuccess, userError, paymentMethod: undefined };
    },
    [submitPaymentMethodCardMutation, formatMessage],
  );

  // ─── Hybrid Payment Method Submission ────────────────────────────────

  const submitPaymentMethod = useCallback(
    async (payload: SubmitPaymentMethodPayload) => {
      const { isSuccess, userError, paymentMethod } = await submitPaymentMethodCard(payload);

      if (isSuccess) {
        track('payment_credit.save_card');
        addNoticeBanner({
          text: formatMessage(messages.creditCardFormSaveSuccess),
          palette: 'success',
        });

        onSuccess(paymentMethod);

        return;
      }

      track('payment_credit.save_card_failed', { userError });
      addNoticeBanner({ text: userError, palette: 'caution' });
    },
    [formatMessage, track, addNoticeBanner, submitPaymentMethodCard, onSuccess],
  );

  return {
    submitPaymentMethod,
    isSubmitting: submitPaymentMethodCardResponse.fetching,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormErrorSave: {
    defaultMessage: 'We were unable to save your card information. Please try again.',
    description: 'Credit Card Form | Error | Save',
  },
  creditCardFormSaveSuccess: {
    defaultMessage: 'Payment method added successfully.',
    description: 'Credit Card Form | Save | Success',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SubmitPaymentMethodProps = Readonly<{
  onSuccess: (paymentMethod: PaymentMethodCard) => void;
}>;

type SubmitPaymentMethodPayload = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
}>;
